import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { QrCode, Plus, Trash2, Search, RefreshCw, CheckCircle, XCircle, FileText, AlertTriangle, ChevronLeft, ChevronRight, Link2, Shield, ExternalLink, Download, Filter, Zap } from 'lucide-react';
import { getToken } from '../services/tokenService';
import { docTypeLabel } from './qrRegistryShared';
import QRRegistryForm from './QRRegistryForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RegistreQRCodes() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalManual, setTotalManual] = useState(0);
  const [totalAuto, setTotalAuto] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    reference: '', target_type: 'souscription', target_id: '', souscription_id: '',
    document_type: 'rcds', description: ''
  });
  const [selectedSub, setSelectedSub] = useState(null);
  const [typeFilters, setTypeFilters] = useState(['souscription', 'ged', 'facture', 'ftpe_adhesion']);
  const [submitting, setSubmitting] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);
  const [verifyRef, setVerifyRef] = useState('');
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [exporting, setExporting] = useState(false);

  const headers = { Authorization: `Bearer ${getToken()}` };

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '30' });
      if (search) params.append('search', search);
      if (sourceFilter) params.append('source', sourceFilter);
      const res = await axios.get(`${BACKEND_URL}/api/admin/qr-codes/all?${params}`, { headers });
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
      setTotalManual(res.data.total_manual || 0);
      setTotalAuto(res.data.total_auto || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch {
      toast.error('Erreur lors du chargement du registre');
    } finally {
      setLoading(false);
    }
  }, [page, search, sourceFilter]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const handleSelectSouscription = (target) => {
    setSelectedSub(target);
    setFormData(prev => ({
      ...prev,
      target_type: target.target_type || 'souscription',
      target_id: target.target_id || target.id,
      souscription_id: target.target_id || target.id, // rétrocompat
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.reference.trim()) {
      toast.error('La reference QR est obligatoire');
      return;
    }
    if (!formData.target_id.trim()) {
      toast.error('Veuillez selectionner une cible (souscription, GED, facture, adhesion)');
      return;
    }
    setSubmitting(true);
    setLastCreated(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/qr-codes`, formData, {
        headers: { ...headers, 'Content-Type': 'application/json' }
      });
      setLastCreated(res.data.entry);
      toast.success('Reference QR enregistree et verifiable !');
      setPage(1);
      loadEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'ajout');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetForm = () => {
    setFormData({ reference: '', target_type: 'souscription', target_id: '', souscription_id: '', document_type: 'rcds', description: '' });
    setSelectedSub(null);
    setLastCreated(null);
    setShowForm(false);
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Supprimer la reference "${entry.reference}" du registre ?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/qr-codes/${entry.id}`, { headers });
      toast.success('Reference supprimee');
      loadEntries();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleTestVerify = async (entry) => {
    setTestingId(entry.id);
    try {
      const docType = entry.document_type || 'rcds';
      const res = await axios.get(`${BACKEND_URL}/api/verifier/${docType}/${entry.reference}`);
      if (res.data.valide) {
        toast.success(`QR "${entry.reference}" est verifiable !`);
      } else {
        toast.error(`QR "${entry.reference}" n'est PAS verifiable`);
      }
    } catch {
      toast.error('Erreur lors du test');
    } finally {
      setTestingId(null);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (sourceFilter) params.append('source', sourceFilter);
      const res = await axios.get(`${BACKEND_URL}/api/admin/qr-codes/export-csv?${params}`, {
        headers, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = res.headers['content-disposition'] || '';
      const filename = disposition.match(/filename="(.+)"/)?.[1] || 'registre_qr_codes.csv';
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export CSV telecharge');
    } catch {
      toast.error('Erreur export CSV');
    } finally {
      setExporting(false);
    }
  };

  const handleVerify = async () => {
    if (!verifyRef.trim()) return;
    setVerifyLoading(true);
    setVerifyResult(null);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/verifier/rcds/${verifyRef.trim()}`);
      setVerifyResult(res.data);
    } catch {
      setVerifyResult({ valide: false, message: 'Erreur de verification' });
    } finally {
      setVerifyLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="qr-registry-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="qr-registry-title">
            <QrCode className="h-7 w-7 text-indigo-600" />
            Registre QR Codes
          </h2>
          <p className="text-gray-500 mt-1 text-sm">
            Vue unifiee des QR codes. Liez manuellement un QR a une souscription pour le rendre verifiable.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={loadEntries} disabled={loading} data-testid="qr-registry-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          <Button
            variant="outline"
            onClick={handleExportCSV}
            disabled={exporting}
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="qr-registry-export-btn"
          >
            {exporting ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Export CSV
          </Button>
          <Button onClick={() => { setShowForm(!showForm); setLastCreated(null); }} className="bg-indigo-600 hover:bg-indigo-700" data-testid="qr-registry-add-btn">
            <Plus className="h-4 w-4 mr-2" />
            Lier un QR Code
          </Button>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card
          className={`cursor-pointer transition-all ${sourceFilter === '' ? 'ring-2 ring-indigo-400 border-indigo-300' : 'border-indigo-200 bg-indigo-50/50 hover:border-indigo-300'}`}
          onClick={() => { setSourceFilter(''); setPage(1); }}
          data-testid="qr-filter-all"
        >
          <CardContent className="pt-4 pb-3 px-4 flex items-center justify-between">
            <div>
              <div className="text-sm text-indigo-600 font-medium">Total</div>
              <div className="text-2xl font-bold text-indigo-800">{total}</div>
            </div>
            <QrCode className="h-8 w-8 text-indigo-300" />
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${sourceFilter === 'automatique' ? 'ring-2 ring-blue-400 border-blue-300' : 'border-blue-200 bg-blue-50/50 hover:border-blue-300'}`}
          onClick={() => { setSourceFilter(sourceFilter === 'automatique' ? '' : 'automatique'); setPage(1); }}
          data-testid="qr-filter-auto"
        >
          <CardContent className="pt-4 pb-3 px-4 flex items-center justify-between">
            <div>
              <div className="text-sm text-blue-600 font-medium">Automatiques</div>
              <div className="text-2xl font-bold text-blue-800">{totalAuto}</div>
            </div>
            <CheckCircle className="h-8 w-8 text-blue-300" />
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${sourceFilter === 'manuel' ? 'ring-2 ring-amber-400 border-amber-300' : 'border-amber-200 bg-amber-50/50 hover:border-amber-300'}`}
          onClick={() => { setSourceFilter(sourceFilter === 'manuel' ? '' : 'manuel'); setPage(1); }}
          data-testid="qr-filter-manual"
        >
          <CardContent className="pt-4 pb-3 px-4 flex items-center justify-between">
            <div>
              <div className="text-sm text-amber-600 font-medium">Manuels</div>
              <div className="text-2xl font-bold text-amber-800">{totalManual}</div>
            </div>
            <Link2 className="h-8 w-8 text-amber-300" />
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="pt-4 pb-3 px-4 flex items-center justify-between">
            <div>
              <div className="text-sm text-emerald-600 font-medium">Verifiables</div>
              <div className="text-2xl font-bold text-emerald-800">
                {entries.filter(e => e.souscription_found).length} / {entries.length}
              </div>
            </div>
            <Shield className="h-8 w-8 text-emerald-300" />
          </CardContent>
        </Card>
      </div>

      {/* Add form - enhanced */}
      {showForm && (
        <QRRegistryForm
          formData={formData}
          setFormData={setFormData}
          selectedSub={selectedSub}
          setSelectedSub={setSelectedSub}
          typeFilters={typeFilters}
          setTypeFilters={setTypeFilters}
          handleSubmit={handleSubmit}
          handleResetForm={handleResetForm}
          handleSelectSouscription={handleSelectSouscription}
          submitting={submitting}
          lastCreated={lastCreated}
          setLastCreated={setLastCreated}
          headers={headers}
        />
      )}

      {/* Quick verify */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="h-5 w-5 text-green-600" />
            Verification rapide
          </CardTitle>
          <CardDescription>Testez si une reference QR est verifiable dans le systeme</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <input
              type="text"
              value={verifyRef}
              onChange={e => setVerifyRef(e.target.value)}
              placeholder="Saisir une reference QR..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
              onKeyDown={e => e.key === 'Enter' && handleVerify()}
              data-testid="qr-verify-input"
            />
            <Button onClick={handleVerify} disabled={verifyLoading} className="bg-green-600 hover:bg-green-700" data-testid="qr-verify-btn">
              {verifyLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
              Verifier
            </Button>
          </div>
          {verifyResult && (
            <div className={`mt-3 p-3 rounded-lg border ${verifyResult.valide ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`} data-testid="qr-verify-result">
              <div className="flex items-center gap-2">
                {verifyResult.valide
                  ? <CheckCircle className="h-5 w-5 text-green-600" />
                  : <XCircle className="h-5 w-5 text-red-600" />}
                <span className={`font-semibold ${verifyResult.valide ? 'text-green-700' : 'text-red-700'}`}>
                  {verifyResult.valide ? 'Document Authentique' : 'Document Non Verifie'}
                </span>
              </div>
              {verifyResult.valide && (
                <div className="mt-2 text-sm text-gray-600 grid grid-cols-2 gap-1">
                  {(verifyResult.ref_solidaire || verifyResult.numero_police) && <span>Ref. Solidaire: {verifyResult.ref_solidaire || verifyResult.numero_police}</span>}
                  {verifyResult.entreprise && <span>Entreprise: {verifyResult.entreprise}</span>}
                  {verifyResult.type_document && <span>Type: {verifyResult.type_document}</span>}
                  {verifyResult.source && <span>Source: {verifyResult.source}</span>}
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">{verifyResult.message}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Search bar */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Rechercher par reference, Ref. Solidaire, entreprise..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
            data-testid="qr-registry-search"
          />
        </div>
        <div className="flex items-center gap-3">
          {sourceFilter && (
            <Badge variant="outline" className="px-2 py-1 cursor-pointer hover:bg-gray-100 text-slate-800 font-semibold" onClick={() => { setSourceFilter(''); setPage(1); }}>
              <Filter className="h-3 w-3 mr-1" />
              {sourceFilter === 'manuel' ? 'Manuels' : 'Automatiques'}
              <XCircle className="h-3 w-3 ml-1.5 text-gray-400" />
            </Badge>
          )}
          <Badge variant="secondary" className="px-3 py-1.5" data-testid="qr-registry-total">
            <FileText className="h-3.5 w-3.5 mr-1.5" />
            {total} entree{total > 1 ? 's' : ''}
          </Badge>
        </div>
      </div>

      {/* Table */}
      <Card data-testid="qr-registry-table-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="h-8 w-8 animate-spin text-indigo-400" />
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-16 text-gray-500" data-testid="qr-registry-empty">
              <QrCode className="h-16 w-16 mx-auto mb-4 opacity-20" />
              <p className="font-medium">Aucune entree dans le registre</p>
              <p className="text-sm mt-1">
                {search ? 'Aucun resultat pour cette recherche' : 'Cliquez sur "Lier un QR Code" pour commencer'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Reference QR</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Souscription</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Source</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Statut</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {entries.map(entry => (
                    <tr key={entry.id} className="hover:bg-gray-50/50 transition-colors" data-testid={`qr-registry-row-${entry.id}`}>
                      <td className="px-4 py-3">
                        <code className="text-sm bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded font-mono">
                          {entry.reference}
                        </code>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          {entry.souscription_info ? (
                            <>
                              <p className="font-medium text-gray-900">{entry.souscription_info.ref_solidaire || entry.souscription_id}</p>
                              {entry.souscription_info.raison_sociale && (
                                <p className="text-xs text-gray-500 mt-0.5">{entry.souscription_info.raison_sociale}</p>
                              )}
                            </>
                          ) : (
                            <p className="text-gray-500">{entry.souscription_id}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="text-xs">{docTypeLabel(entry.document_type)}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        {entry.source === 'automatique' ? (
                          <Badge className="bg-blue-700 text-white border-blue-900 text-xs font-semibold">
                            <CheckCircle className="h-3 w-3 mr-1" />Auto
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-600 text-white border-amber-800 text-xs font-semibold">
                            <Link2 className="h-3 w-3 mr-1" />Manuel
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {entry.souscription_found ? (
                          <Badge className="bg-emerald-700 text-white border-emerald-900 text-xs font-semibold">
                            <CheckCircle className="h-3 w-3 mr-1" />Verifiable
                          </Badge>
                        ) : (
                          <Badge className="bg-red-700 text-white border-red-900 text-xs font-semibold">
                            <AlertTriangle className="h-3 w-3 mr-1" />Non liee
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                        {entry.created_at
                          ? new Date(entry.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                          : '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex gap-1 justify-center">
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => handleTestVerify(entry)}
                            disabled={testingId === entry.id}
                            title="Tester la verification"
                            data-testid={`qr-registry-test-${entry.id}`}
                          >
                            {testingId === entry.id
                              ? <RefreshCw className="h-4 w-4 animate-spin text-green-600" />
                              : <Zap className="h-4 w-4 text-green-600" />}
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => window.open(`${BACKEND_URL}/api/verify-page/${entry.document_type}/${entry.reference}`, '_blank')}
                            title="Page de verification"
                            data-testid={`qr-registry-view-${entry.id}`}
                          >
                            <ExternalLink className="h-4 w-4 text-blue-600" />
                          </Button>
                          {entry.source === 'manuel' && (
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => handleDelete(entry)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`qr-registry-delete-${entry.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50/50" data-testid="qr-registry-pagination">
              <p className="text-sm text-gray-600">Page {page} sur {totalPages} ({total} entrees)</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
